import { type BoardVisit, withSurname } from "@board/boardVisit";
import {
  type BoardCardGroup,
  dvmNames,
  siblingMark,
} from "@board/groupBoardVisits";
import { type Lane, type Position, TERMINAL_POSITIONS } from "@board/positions";
import {
  BoardCard,
  type BoardDelay,
  ClockIcon,
  formatClockTime,
  type SpriteTraits,
} from "@scribenote/brian-design-system";
import { formatMonthDay } from "@utils/format-date";
import type { TodayVisit } from "@visits/visitsClient";
import type { ReactNode } from "react";

export interface PlacedVisit<V extends BoardVisit = BoardVisit> {
  visit: V;
  lane: Lane;
  position: Position;
  behindMinutes: number | null;
}

type CardTime = { prefix: string | null; at: string };

const positionTime = ({
  visit,
  lane,
  position,
}: PlacedVisit): CardTime | null => {
  if (position === "scheduled")
    return visit.scheduledStart
      ? { prefix: null, at: visit.scheduledStart }
      : null;
  const terminal = TERMINAL_POSITIONS.has(position);
  if (lane === "dropoff" && visit.scheduledEnd && !terminal)
    return { prefix: "pickup", at: visit.scheduledEnd };
  return visit.progressAt
    ? { prefix: terminal ? null : "since", at: visit.progressAt }
    : null;
};

const localDay = (at: string | number): string => new Date(at).toDateString();

const startedEarlier = (visit: BoardVisit, now: number): boolean =>
  visit.board === "open" ||
  (visit.scheduledStart !== null &&
    Date.parse(visit.scheduledStart) < now &&
    localDay(visit.scheduledStart) !== localDay(now));

/** Earlier-day visits show their dated booking, not a delay counted in days. */
export const cardTime = (placed: PlacedVisit, now: number): CardTime | null => {
  const time = positionTime(placed);
  const { visit } = placed;
  if (time || !visit.scheduledStart || !startedEarlier(visit, now)) return time;
  return { prefix: null, at: visit.scheduledStart };
};

const BoardTime = ({ time, now }: { time: CardTime; now: number }) => {
  const date = new Date(time.at);
  const sameDay = localDay(time.at) === localDay(now);
  return (
    <time
      dateTime={date.toISOString()}
      className="inline-flex items-center whitespace-nowrap"
    >
      <ClockIcon className="mr-[0.35em]" />
      {[time.prefix, sameDay ? formatClockTime(date) : formatMonthDay(time.at)]
        .filter(Boolean)
        .join(" ")}
    </time>
  );
};

const surnameOf = (clientName: string | null): string | null => {
  const name = clientName?.trim();
  if (!name) return null;
  if (name.includes(",")) return name.split(",")[0].trim() || null;
  return name.split(/\s+/).at(-1) ?? null;
};

export const boardName = (visit: TodayVisit): string =>
  withSurname(visit.patientName, surnameOf(visit.clientName));

/**
 * One card per household: the lead visit's time and delay, the household label
 * over the pets' names when the card holds more than one, and "with Tofu" when
 * a pet is elsewhere. A household card opens its pets by name, never as a whole.
 */
export const VisitCard = ({
  card,
  now,
  sprites,
  who,
  onOpenPatient,
}: {
  card: BoardCardGroup;
  now: number;
  /** In `card.visits` order. */
  sprites: readonly (SpriteTraits | null | undefined)[];
  /** Replaces the plain room and DVM line. */
  who?: ReactNode;
  onOpenPatient?: (patientId: string) => void;
}) => {
  const { visits, elsewhere } = card;
  const [lead] = visits;
  const { visit } = lead;
  const household = visits.length > 1;
  // The latest pet sets the card's delay.
  const behindMinutes = Math.max(...visits.map((p) => p.behindMinutes ?? 0));
  const open = household ? undefined : onOpenPatient;
  const plainWho = [
    visit.roomName,
    ...dvmNames(visits.map((p) => p.visit.dvmName)),
  ]
    .filter(Boolean)
    .join(" · ");
  const delay: BoardDelay | undefined =
    behindMinutes && !startedEarlier(visit, now)
      ? { minutes: behindMinutes, direction: "behind" }
      : undefined;
  // One prefixed time or a delay, not both.
  const found = cardTime(lead, now);
  const time = delay && found?.prefix ? null : found;

  return (
    <BoardCard
      name={household ? visit.householdLabel : visit.name}
      mark={siblingMark(elsewhere)}
      pets={
        household
          ? visits.map((p) => ({
              key: p.visit.id,
              name: p.visit.patientName,
              onOpen: onOpenPatient && (() => onOpenPatient(p.visit.patientId)),
            }))
          : undefined
      }
      who={who ?? (plainWho || undefined)}
      time={time ? <BoardTime time={time} now={now} /> : undefined}
      sprites={sprites}
      delay={delay}
      onSelect={open && (() => open(visit.patientId))}
      label={open && `Open ${visit.name}`}
    />
  );
};
